/**
 * components/accounting/ReportLedger.tsx
 * Bank ledger report (debit/credit) generated from deposited sales and paid expenses.
 * - Debit (مدين): increases bank (deposited sales)
 * - Credit (دائن): decreases bank (paid expenses)
 * Provides PDF export via print-ready window.
 * Uses Gregorian dates for display.
 * - Footer signature in both on-screen and PDF.
 * - Visible for admin and viewer (entry users hidden).
 */

import { useMemo } from 'react'
import { useAccountingStore } from '../../store/accounting'
import { formatCurrency, formatDate } from '../../lib/format'
import { FileText } from 'lucide-react'
import { useAuthStore } from '../../store/auth'
import { useUserStore } from '@/store/user'

interface LedgerEntry {
  date: string
  description: string
  debit: number
  credit: number
}

/**
 * ReportLedger
 * Produces a running ledger and PDF export (print window).
 * Visible for admin and viewer. Entry users cannot see it.
 */
export function ReportLedger() {
  const transactions = useUserStore((s) => s.transactions)
  const expenses = useUserStore((s) => s.expenses)
  

  //const transactions = useAccountingStore((s) => s.transactions)
  //const expenses = useAccountingStore((s) => s.expenses)
  //const currentBalance = useAccountingStore((s) => s.currentAccountBalance)
  
  const calculateStoreBalance = useUserStore((s) => s.getStoreBalance)
  const currentBalance = useMemo(() => {
    return calculateStoreBalance()
  }, [transactions, expenses])
  //const currentUser = useAuthStore((s) => s.getCurrentUser())
  const role = useUserStore((s) => s.role)

  if ((role !== 'admin' && role !== 'viewer')) {
    return null
  }

  const entries = useMemo<LedgerEntry[]>(() => {
    const fromSales = transactions
      .filter((t) => t.deposited && t.deposit_date)
      .map<LedgerEntry>((t) => ({
        date: t.deposit_date!,
        description: `إيداع ${t.gateway}${t.description ? ` - ${t.description}` : ''}`,
        debit: t.amount_gross,
        credit: 0,
      }))
    const fromExpenses = expenses
      .filter((e) => e.paid && e.payment_date)
      .map<LedgerEntry>((e) => ({
        date: e.payment_date!,
        description: `مصروف ${e.category}${e.description ? ` - ${e.description}` : ''}${e.payment_method ? ` (${e.payment_method})` : ''}`,
        debit: 0,
        credit: e.amount,
      }))
    return [...fromSales, ...fromExpenses].sort((a, b) => (a.date > b.date ? 1 : -1))
  }, [transactions, expenses])

  const rows = useMemo(() => {
    let running = 0
    return entries.map((e) => {
      running += e.debit - e.credit
      return { ...e, running }
    })
  }, [entries])

  const exportPDF = () => {
    const title = 'تقرير البنك (مدين/دائن)'
    const styles = `
      <style>
        @media print {
          @page { size: A4; margin: 16mm; }
        }
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, 'Noto Kufi Arabic', Arial, sans-serif; background: #fff; color: #0f172a; }
        h1 { font-size: 20px; margin: 0 0 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; }
        th { background: #f3f4f6; text-align: right; }
        tfoot td { font-weight: 600; }
        .meta { margin-top: 8px; font-size: 12px; color: #334155; }
        .sig { margin-top: 16px; font-size: 12px; color: #334155; text-align: center; }
      </style>
    `
    let running = 0
    const rowsHtml = entries
      .map((e) => {
        running += e.debit - e.credit
        const dateStr = new Date(e.date).toLocaleDateString('ar', { calendar: 'gregory' })
        return `
        <tr>
          <td>${dateStr}</td>
          <td>${e.description.replace(/</g, '&lt;')}</td>
          <td>${e.debit ? e.debit.toFixed(2) : '-'}</td>
          <td>${e.credit ? e.credit.toFixed(2) : '-'}</td>
          <td>${running.toFixed(2)}</td>
        </tr>
      `
      })
      .join('')

    const nowStr = new Date().toLocaleString('ar', { calendar: 'gregory' })

    const footerSig = 'صنع بحب من مؤسسة ابعاد الإستراتيجية — سجل تجاري رقم 7051346430'

    const doc = `
      <html lang="ar" dir="rtl">
        <head>
          <meta charSet="utf-8" />
          <title>${title}</title>
          ${styles}
        </head>
        <body>
          <h1>${title}</h1>
          <table>
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>الوصف</th>
                <th>مدين</th>
                <th>دائن</th>
                <th>الرصيد</th>
              </tr>
            </thead>
            <tbody>${rowsHtml || `<tr><td colspan="5" style="text-align:center;color:#64748b;">لا توجد قيود بعد.</td></tr>`}</tbody>
          </table>
          <div class="meta">
            <div>الرصيد الحالي وفقاً للقيود: <strong>${(rows.at(-1)?.running ?? 0).toFixed(2)}</strong></div>
            <div>الرصيد المسجّل في النظام: <strong>${(currentBalance ?? 0).toFixed(2)}</strong></div>
            <div>تاريخ التوليد: ${nowStr}</div>
          </div>
          <div class="sig">${footerSig}</div>
          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
              }, 100);
            }
          </script>
        </body>
      </html>
    `
    const w = window.open('', '_blank')
    if (!w) return
    w.document.open()
    w.document.write(doc)
    w.document.close()
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">تقرير البنك (مدين/دائن)</h2>
        <button
          onClick={exportPDF}
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-2 text-white hover:bg-slate-800"
        >
          <FileText className="h-4 w-4" /> تصدير PDF
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-right font-medium text-gray-700">التاريخ</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الوصف</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">مدين</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">دائن</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الرصيد</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  لا توجد قيود بعد.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 text-gray-700">{formatDate(r.date)}</td>
                  <td className="px-3 py-2">{r.description}</td>
                  <td className="px-3 py-2">{r.debit ? formatCurrency(r.debit) : '-'}</td>
                  <td className="px-3 py-2">{r.credit ? formatCurrency(r.credit) : '-'}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{formatCurrency((r as any).running)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
        <p>
          الرصيد الحالي وفقاً للقيود:{' '}
          <span className="font-semibold">{formatCurrency((rows.at(-1) as any)?.running || 0)}</span>
        </p>
        <p>
          الرصيد المسجّل في النظام: <span className="font-semibold">{formatCurrency(currentBalance)}</span>
        </p>
        <p className="mt-2 text-center text-slate-600">
          صنع بحب من مؤسسة ابعاد الإستراتيجية — سجل تجاري رقم 7051346430
        </p>
      </div>
    </div>
  )
}
